/**
 * Guest-first access — the browser side of the guest→account handoff.
 *
 * When a GUEST starts signing in with a different identity, the browser must
 * remember which guest it was so the account can claim that guest's history
 * afterwards. Sign-in may involve a full-page OAuth redirect, so memory is not
 * enough; the handoff is kept in `sessionStorage` (tab-scoped, gone when the
 * tab closes).
 *
 * It carries the guest user id (a public identifier), the server-issued
 * single-use TICKET, and the conversation the guest was reading. It carries
 * NO access or refresh token, NO transcript, and NO authority: the server
 * verifies the ticket against a stored hash, and the claim copies from the
 * hosted authority only.
 */

const KEY = "looseend.guest-handoff.v1";
const TTL_MS = 30 * 60 * 1000;

export interface GuestHandoff {
  readonly guestUserId: string;
  readonly ticket: string;
  readonly conversationId: string | null;
  readonly expiresAt: number;
}

function storage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function stashGuestHandoff(input: {
  readonly guestUserId: string;
  readonly ticket: string;
  readonly conversationId?: string | null;
  readonly now?: number;
}): void {
  const store = storage();
  if (!store) return;
  const handoff: GuestHandoff = {
    guestUserId: input.guestUserId,
    ticket: input.ticket,
    conversationId: input.conversationId ?? null,
    expiresAt: (input.now ?? Date.now()) + TTL_MS,
  };
  try {
    store.setItem(KEY, JSON.stringify(handoff));
  } catch {
    // Storage full or blocked: sign-in still works, the claim is just not offered.
  }
}

export function readGuestHandoff(now: number = Date.now()): GuestHandoff | null {
  const store = storage();
  if (!store) return null;
  const raw = store.getItem(KEY);
  if (!raw) return null;
  const parsed = parseHandoff(raw);
  if (!parsed || parsed.expiresAt <= now) {
    store.removeItem(KEY);
    return null;
  }
  return parsed;
}

export function clearGuestHandoff(): void {
  storage()?.removeItem(KEY);
}

export function parseHandoff(raw: string): GuestHandoff | null {
  try {
    const value = JSON.parse(raw) as Record<string, unknown>;
    if (!value || typeof value !== "object") return null;
    const guestUserId = value["guestUserId"];
    const ticket = value["ticket"];
    const conversationId = value["conversationId"];
    const expiresAt = value["expiresAt"];
    if (typeof guestUserId !== "string" || guestUserId.length === 0) return null;
    if (typeof ticket !== "string" || ticket.length === 0) return null;
    if (typeof expiresAt !== "number" || !Number.isFinite(expiresAt)) return null;
    return {
      guestUserId,
      ticket,
      conversationId: typeof conversationId === "string" ? conversationId : null,
      expiresAt,
    };
  } catch {
    return null;
  }
}

/** Extracts `/app/chat/<id>` from a pathname, else null. */
export function conversationIdFromPath(pathname: string): string | null {
  const match = /^\/app\/chat\/([^/?#]+)/.exec(pathname);
  return match?.[1] ?? null;
}
